import { tw } from "@aet/tailwind/µ"
import { Chip, Input, Listbox, ListboxItem } from "@heroui/react"
import { useLingui } from "@lingui/react/macro"
import { clsx } from "clsx"
import { Search } from "lucide-react"
import { useMemo, useState } from "react"

import { FileIcon } from "#/components/FileIcon"
import { getSelectionKey, id } from "#/utils"

import type { Entry } from "../har"

import { getMethodColor, getStatusColor } from "./StatusChip"
import { URLView } from "./URLView"

export function EntityList({
  compact,
  selected,
  setSelected,
  entries,
}: {
  compact: boolean
  selected: string | undefined
  setSelected: (id: string) => void
  entries: Entry[]
}) {
  const { t } = useLingui()
  const [search, setSearch] = useState("")
  const indices = useMemo(() => {
    const map = new Map<string, number>()
    for (const [index, entry] of entries.entries()) {
      map.set(id(entry), index)
    }
    return map
  }, [entries])

  const filtered = useMemo(() => {
    if (!search) {
      return entries
    }

    const num = search.match(/^#(\d+)$/)
    const prefix: Entry[] = []
    if (num) {
      const item = entries[Number(num[1])]
      if (item) {
        prefix.push(item)
      }
    }

    let keyword = search.toLowerCase()
    let mustInclude = true
    if (keyword[0] === "-") {
      mustInclude = false
      keyword = keyword.slice(1)
    }

    return prefix.concat(
      entries.filter((entry, i) => {
        const includes = entry.request.url.toLowerCase().includes(keyword)
        return (
          includes === mustInclude && (!prefix.length || i !== indices.get(id(prefix[0])))
        )
      })
    )
  }, [entries, search, indices])

  return (
    <>
      <div className="mt-1 w-full p-1">
        <div css="mx-2">
          <Input
            css="w-full"
            placeholder={t`Search`}
            startContent={<Search size={19} />}
            type="search"
            value={search}
            variant="bordered"
            classNames={{
              inputWrapper: tw`border bg-white dark:bg-black`,
            }}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div css="group ml-1.5 mr-1 overflow-scroll">
        <Listbox
          aria-label={t`Request list`}
          items={filtered}
          selectedKeys={selected ? [selected] : []}
          selectionMode="single"
          onSelectionChange={id => setSelected(getSelectionKey(id))}
        >
          {entry => (
            <ListboxItem
              aria-label={entry.request.url}
              key={id(entry)}
              role="listitem"
              className={clsx(
                "px[2] py[1] relative rounded transition-all selected:bg-gray-200 dark:selected:bg-gray-800",
                compact && "[&>span]:overflow-hidden"
              )}
            >
              <Chip
                color="secondary"
                css="absolute -left-1 -top-1 z-10 hidden opacity-90 hover:opacity-100 group-hover:inline-flex"
                radius="sm"
                size="sm"
              >
                #{indices.get(id(entry))}
              </Chip>
              <div
                className={clsx(compact && "overflow-hidden")}
                css="flex items-center gap-1"
              >
                <Chip
                  color={getStatusColor(entry.response.status)}
                  css="tabular-nums"
                  radius="sm"
                  size="sm"
                  variant="flat"
                >
                  {entry.response.status}
                </Chip>
                {entry.request.method !== "GET" && (
                  <Chip
                    color={getMethodColor(entry.request.method)}
                    css="tabular-nums"
                    radius="sm"
                    size="sm"
                    variant="flat"
                  >
                    {entry.request.method}
                  </Chip>
                )}
                <FileIcon mimeType={entry.response.content.mimeType} />
                <URLView compact={compact} url={entry.request.url} />
              </div>
            </ListboxItem>
          )}
        </Listbox>
      </div>
    </>
  )
}
